import { HymnPageLayout } from "@/components/hymns/HymnPageLayout";
import { getHymnsBySection, Hymn } from "@/services/hymnsApi";
import { getHymnSlug, getLanguageLabel, scrollToTop } from "@/utils";
import {
  AlertTriangle,
  ArrowLeft,
  BookOpen,
  ChevronRight,
  Music,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

const PageChantsSectionsGrid = () => {
  const { sectionCode } = useParams<{ sectionCode: string }>();
  const [searchParams] = useSearchParams();
  const langParam = searchParams.get("lang") || "";

  const navigate = useNavigate();

  const [hymns, setHymns] = useState<Hymn[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const [filterQuery, setFilterQuery] = useState<string>("");

  const loadData = useCallback(async () => {
    if (!sectionCode) return;
    setLoading(true);
    setHasError(false);
    try {
      const hymnsData = await getHymnsBySection(
        sectionCode,
        langParam || undefined,
        600,
      );
      setHymns(hymnsData);
    } catch (err) {
      console.error("Error loading section hymns:", err);
      setHasError(true);
    } finally {
      setLoading(false);
    }
  }, [sectionCode, langParam]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter hymns by number or title
  const filteredHymns = useMemo(() => {
    if (!filterQuery.trim()) return hymns;
    const q = filterQuery.toLowerCase().trim();
    return hymns.filter((h) => {
      const matchNum = String(h.number).includes(q);
      const matchTitle = h.title.toLowerCase().includes(q);
      const matchCategory = h.category?.toLowerCase().includes(q);
      return matchNum || matchTitle || matchCategory;
    });
  }, [hymns, filterQuery]);

  const rawSectionName = hymns[0]?.section || sectionCode || "";
  const cleanSectionName = rawSectionName
    .replace(/\s*\([^)]*\)\s*$/, "")
    .trim();
  const effectiveLang = langParam || hymns[0]?.language || "";

  const badgeInfo = {
    badge: sectionCode ? sectionCode.split("-")[0] : "",
    name: cleanSectionName,
    languageName: getLanguageLabel(effectiveLang),
    languageCode: effectiveLang,
    hymnTotal: hymns.length,
  };

  const handleHymnClick = (
    number: number,
    title?: string,
    hymnSectionCode?: string,
  ) => {
    scrollToTop();
    const slug = getHymnSlug(number, title);
    const targetSection = hymnSectionCode || sectionCode || "";
    navigate(
      `/chants-desperance/${encodeURIComponent(targetSection)}/${slug}`,
    );
  };

  const canonicalUrl =
    typeof window !== "undefined" ? window.location.href : "";
  const sectionTitle = `${badgeInfo.name}${badgeInfo.languageName ? ` (${badgeInfo.languageName})` : ""} - Chants d'Espérance | Église Garizim`;
  const sectionDescription = `Liste complète des cantiques du recueil ${badgeInfo.name}${badgeInfo.languageName ? ` en ${badgeInfo.languageName}` : ""} (${sectionCode}) pour l'adoration et la louange.`;

  return (
    <HymnPageLayout>
      <Helmet>
        <title>{sectionTitle}</title>
        <meta name="description" content={sectionDescription} />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:title" content={sectionTitle} />
        <meta property="og:description" content={sectionDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Église Garizim" />
        <meta
          property="og:image"
          content="https://ik.imagekit.io/4apzi8lz0/egarizim/chant-esperance-api.jpg"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={sectionTitle} />
        <meta name="twitter:description" content={sectionDescription} />
        <meta
          name="twitter:image"
          content="https://ik.imagekit.io/4apzi8lz0/egarizim/chant-esperance-api.jpg"
        />
      </Helmet>

      <div className="container mmax-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-6">
        {/* Navigation Breadcrumb */}
        <div className="flex items-center gap-3 mb-4 sm:mb-8">
          <Link
            to="/chants-desperance"
            onClick={() => scrollToTop()}
            className="inline-flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-3.5 sm:py-2 rounded-lg sm:rounded-xl bg-slate-900/80 hover:bg-slate-800 border border-slate-700/80 text-slate-300 hover:text-white text-xs sm:text-sm font-semibold backdrop-blur-md transition-all group"
          >
            <ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4 group-hover:-translate-x-1 transition-transform" />
            <span>Tous les recueils</span>
          </Link>
        </div>

        {/* Section Header Banner */}
        <div className="relative rounded-2xl sm:rounded-3xl bg-slate-900/90 border border-slate-700/70 p-4 sm:p-8 md:p-10 shadow-xl sm:shadow-2xl backdrop-blur-xl mb-6 sm:mb-10 overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-sky-500/10 rounded-full blur-3xl pointer-events-none" />

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 sm:gap-6 relative z-10">
            <div className="flex items-center gap-3 sm:gap-5">
              <div className="w-12 h-12 sm:w-20 sm:h-20 rounded-xl sm:rounded-2xl bg-gradient-to-br from-sky-600 to-sky-950 border border-sky-400/40 flex items-center justify-center flex-shrink-0 shadow-lg sm:shadow-xl shadow-sky-500/20">
                <span className="font-serif font-black text-xl sm:text-3xl text-sky-300">
                  {badgeInfo.badge}
                </span>
              </div>

              <div>
                <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-1 sm:mb-1.5">
                  <span className="px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-semibold bg-sky-950/80 border border-sky-500/30 text-sky-300">
                    {sectionCode}
                  </span>
                  {badgeInfo.languageName && (
                    <span className="px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs font-bold bg-amber-950/80 border border-amber-500/40 text-amber-300">
                      {badgeInfo.languageName}
                    </span>
                  )}
                </div>

                <h1 className="text-xl sm:text-3xl md:text-4xl font-extrabold text-white tracking-tight">
                  {badgeInfo.name}
                </h1>
                <p className="text-slate-400 text-[11px] sm:text-sm mt-0.5 sm:mt-1">
                  {hymns.length} cantiques disponibles dans ce recueil
                </p>
              </div>
            </div>

            {/* Quick Filter Search Input within Section & Clear Button */}
            <div className="w-full md:w-auto flex items-center gap-2">
              <div className="relative flex-1 md:w-72 lg:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400" />
                <input
                  type="text"
                  value={filterQuery}
                  onChange={(e) => setFilterQuery(e.target.value)}
                  placeholder="Rechercher par numéro ou titre..."
                  className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-2.5 rounded-xl bg-slate-900/100 border border-slate-700 text-white placeholder-slate-400 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-sky-400/50"
                />
              </div>

              <button
                type="button"
                onClick={() => setFilterQuery("")}
                disabled={!filterQuery}
                className={`inline-flex items-center gap-1.5 p-2 sm:px-3.5 sm:py-2.5 rounded-xl border text-xs sm:text-sm font-semibold transition-all shadow-sm flex-shrink-0 ${
                  filterQuery
                    ? "bg-slate-800 hover:bg-rose-950/40 text-slate-200 hover:text-rose-300 border-slate-700 hover:border-rose-500/40 cursor-pointer active:scale-95"
                    : "bg-slate-900/40 text-slate-600 border-slate-800 opacity-40 cursor-not-allowed"
                }`}
                title="Effacer la recherche"
                aria-label="Effacer la recherche"
              >
                <X className="w-4 h-4 sm:w-4.5 sm:h-4.5" />
                <span className="hidden sm:inline">Effacer</span>
              </button>
            </div>
          </div>
        </div>

        {/* Skeleton Loader animé pour la liste des cantiques */}
        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {[...Array(12)].map((_, i) => (
              <div
                key={i}
                className="p-3.5 sm:p-4 rounded-xl sm:rounded-2xl bg-slate-900/80 border border-slate-800 animate-pulse flex items-center justify-between"
              >
                <div className="flex items-center gap-3 w-full">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-slate-800 flex-shrink-0" />
                  <div className="space-y-1.5 w-3/4">
                    <div className="h-3.5 sm:h-4 bg-slate-800 rounded w-4/5" />
                    <div className="h-2.5 sm:h-3 bg-slate-800/60 rounded w-1/2" />
                  </div>
                </div>
                <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-full bg-slate-800 flex-shrink-0" />
              </div>
            ))}
          </div>
        )}

        {/* État d'erreur gracieux */}
        {!loading && hasError && (
          <div className="text-center py-12 sm:py-16 px-6 bg-slate-900/80 rounded-2xl sm:rounded-3xl border border-rose-500/30 backdrop-blur-md shadow-2xl max-w-xl mx-auto my-6">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-rose-500/10 border border-rose-500/30 flex items-center justify-center mx-auto mb-3 sm:mb-4 text-rose-400">
              <AlertTriangle className="w-6 h-6 sm:w-7 sm:h-7" />
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-white mb-1.5 sm:mb-2">
              Erreur de chargement des cantiques
            </h3>
            <p className="text-slate-300 text-xs sm:text-sm mb-5 sm:mb-6 max-w-md mx-auto leading-relaxed">
              Impossible de récupérer la liste des cantiques de ce recueil.
              Veuillez vérifier votre connexion Internet ou réessayer.
            </p>
            <button
              onClick={() => loadData()}
              className="inline-flex items-center gap-2 px-4 py-2 sm:px-5 sm:py-2.5 rounded-xl bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold text-xs sm:text-sm shadow-lg shadow-sky-500/20 transition-all hover:scale-105"
            >
              <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span>Réessayer le chargement</span>
            </button>
          </div>
        )}

        {/* Liste des cantiques */}
        {!loading && !hasError && (
          <>
            <div className="flex items-center justify-between text-xs text-slate-400 font-semibold mb-3 sm:mb-4 px-1">
              <span>{filteredHymns.length} cantiques affichés</span>
              {filterQuery && (
                <button
                  onClick={() => setFilterQuery("")}
                  className="text-sky-400 hover:underline"
                >
                  Effacer le filtre
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 sm:gap-4">
              {filteredHymns.map((hymn) => {
                const hasAudio = Boolean(hymn.media_url);
                const songLang = getLanguageLabel(hymn.language);

                return (
                  <div
                    key={hymn.id || `${hymn.section_code}-${hymn.number}-${hymn.language || ""}`}
                    onClick={() =>
                      handleHymnClick(
                        hymn.number,
                        hymn.title,
                        hymn.section_code,
                      )
                    }
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter")
                        handleHymnClick(
                          hymn.number,
                          hymn.title,
                          hymn.section_code,
                        );
                    }}
                    className="group relative flex items-center justify-between p-3 sm:p-4 rounded-xl sm:rounded-2xl bg-slate-900/85 hover:bg-slate-800 border border-slate-800 hover:border-sky-500/50 shadow-md sm:shadow-lg hover:shadow-sky-500/10 backdrop-blur-md transition-all duration-200 cursor-pointer text-left"
                  >
                    {/* Gauche: Numéro + Titre + Catégorie + Langue */}
                    <div className="flex items-center gap-2.5 sm:gap-3.5 min-w-0 pr-2">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-slate-800 group-hover:bg-sky-950 border border-slate-700 group-hover:border-sky-500/40 flex items-center justify-center flex-shrink-0 transition-colors">
                        <span className="font-bold text-amber-300 group-hover:text-sky-300 text-sm sm:text-xl">
                          {hymn.number}
                        </span>
                      </div>

                      <div className="min-w-0">
                        <h2 className="font-bold text-white text-xs sm:text-base truncate group-hover:text-sky-200 transition-colors leading-snug">
                          {hymn.title}
                        </h2>
                        <div className="flex items-center gap-1.5 sm:gap-2 mt-0.5">
                          {songLang && (
                            <span className="text-[10px] sm:text-[11px] font-semibold text-slate-400">
                              {songLang}
                            </span>
                          )}
                          {hymn.category && (
                            <span className="text-[10px] sm:text-[11px] text-slate-500 truncate block">
                              • {hymn.category}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Droite: Indicateur Audio + Flèche */}
                    <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                      {false && hasAudio && (
                        <span className="inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-950/80 border border-emerald-500/40 text-emerald-300">
                          <Music className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                        </span>
                      )}
                      <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-500 group-hover:text-sky-300 group-hover:translate-x-1 transition-all" />
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Aucun résultat */}
        {!loading && !hasError && filteredHymns.length === 0 && (
          <div className="text-center py-20 bg-slate-900/60 rounded-3xl border border-slate-800">
            <BookOpen className="w-12 h-12 text-slate-500 mx-auto mb-3" />
            <h3 className="text-lg font-bold text-white mb-1">
              Aucun cantique trouvé
            </h3>
            <p className="text-slate-400 text-sm">
              Aucun chant ne correspond à votre recherche "{filterQuery}".
            </p>
          </div>
        )}
      </div>
    </HymnPageLayout>
  );
};

export default PageChantsSectionsGrid;
